import {
  consumerSurfaceRedirect,
  roleHome,
  safeNextPath,
} from "@/modules/identity/surfaces";
import type { WireMe } from "~/auth/me";

/**
 * Who may see which surface, decided in the client (R22 T10).
 *
 * **Every rule here is imported, not restated.** `consumerSurfaceRedirect` is
 * H28, `roleHome` is the log-in screen's happy path, `safeNextPath` is the
 * open-redirect guard, and all three come from `@/modules/identity/surfaces`,
 * which is the same file the Next host and the API Worker compile. A guard that
 * decides "members do not browse the consumer side" from its own copy of the
 * rule is a second implementation waiting to disagree with the first.
 *
 * **These redirects are a courtesy, never a gate.** The server re-asks §5 on
 * every read and every command, so the worst a wrong answer here can do is show
 * somebody the wrong menu. That is the reason this file is allowed to exist at
 * all: it is chrome, and the rule underneath it is enforced elsewhere.
 *
 * Pure on purpose. Routing lives in `guards.tsx`, which does nothing but call
 * these and render a `<Navigate>`.
 */

export type Access =
  | { readonly allowed: true }
  | { readonly allowed: false; readonly redirectTo: string };

const ALLOWED: Access = { allowed: true };
const sendTo = (redirectTo: string): Access => ({ allowed: false, redirectTo });

/** `/login?next=…`, with the return path sanitized by the shared guard. */
export function loginPath(from: string, screen = "/login"): string {
  const next = safeNextPath(from);
  return next && next !== "/" ? `${screen}?next=${encodeURIComponent(next)}` : screen;
}

/**
 * The guest surfaces: landing, search, a Business profile, the booking widget.
 *
 * A logged-out visitor is welcome (H28), which is why `/me` answering "guest"
 * is a success rather than a 401.
 */
export function consumerSurfaceAccess(me: WireMe): Access {
  if (me.kind === "guest") return ALLOWED;
  const redirect = consumerSurfaceRedirect({ kind: me.kind });
  return redirect ? sendTo(redirect) : ALLOWED;
}

/**
 * The Consumer's own screens: bookings, favorites, profile, inbox.
 *
 * A guest is sent to log in **carrying where they were going**, because losing
 * the destination is how somebody ends up on the landing page wondering what
 * they clicked.
 */
export function consumerOnlyAccess(me: WireMe, from: string): Access {
  if (me.kind === "guest") return sendTo(loginPath(from));
  if (me.kind === "consumer") return ALLOWED;
  return sendTo(roleHome(me.kind));
}

/**
 * The business dashboard, plus the H44 password gate the Next layout held.
 *
 * The gate is a redirect rather than an H25-style mask because a masked session
 * would also close the screen that fixes it, and its target sits outside
 * `/business` or this rule would send the screen to itself.
 */
export function businessAccess(me: WireMe): Access {
  if (me.kind === "guest") return sendTo("/login/staff");
  if (me.kind !== "member" && me.kind !== "unaffiliated") {
    return sendTo(roleHome(me.kind));
  }
  if (me.passwordChangeRequired) return sendTo("/staff/password");
  return ALLOWED;
}

/**
 * A clinic's **tools**: services, opening hours, the team, the public profile,
 * the settings hub (H26).
 *
 * Everything `businessAccess` requires, and then the manager tier. A
 * practitioner-tier member is sent to their own home rather than to a log-in
 * screen: they are signed in and in the right place, they simply do not
 * configure the clinic.
 *
 * **Still only chrome.** Every one of these screens' commands re-asks §5 on the
 * server through `requireApiManager`, so this decides which menu somebody sees
 * and never what they may do.
 */
export function managerAccess(me: WireMe): Access {
  const base = businessAccess(me);
  if (!base.allowed) return base;
  // Past businessAccess the caller is a member or unaffiliated. An unaffiliated
  // member has no clinic to configure at all, so the join prompt is their home.
  if (me.kind !== "member") return sendTo("/business");
  return me.tier === "manager" ? ALLOWED : sendTo("/business");
}

/** The admin console. Its own log-in screen, because the identifier is an email. */
export function adminAccess(me: WireMe): Access {
  if (me.kind === "guest") return sendTo("/login/admin");
  return me.kind === "admin" ? ALLOWED : sendTo(roleHome(me.kind));
}

/**
 * The forced password change (H44).
 *
 * Reachable by exactly the two roles that can be made to change a password, and
 * deliberately reachable **while** `passwordChangeRequired` is set, which is
 * what `businessAccess` above cannot allow.
 */
export function passwordChangeAccess(me: WireMe): Access {
  if (me.kind === "guest") return sendTo("/login/staff");
  if (me.kind === "member" || me.kind === "unaffiliated") return ALLOWED;
  return sendTo(roleHome(me.kind));
}

/**
 * The inbox, which belongs to an **account** rather than to a role (R22 T13).
 *
 * A Consumer, a member and an admin all have one: the endpoint behind it takes
 * any account, and the notifications it holds are addressed to a person rather
 * than to a surface. So this is the one guarded screen that asks only "is
 * somebody signed in", and the screen itself renders role-appropriate chrome.
 *
 * A guest is sent to log in carrying the path, exactly as the Consumer's own
 * screens do: a notification link that lands on the landing page has lost the
 * notification.
 */
export function accountOnlyAccess(me: WireMe, from: string): Access {
  return me.kind === "guest" ? sendTo(loginPath(from)) : ALLOWED;
}

/**
 * The ways in: log in, sign up, verify.
 *
 * Somebody already signed in has nothing to do here, and the Next host
 * redirected them home from each of these pages.
 *
 * **It honours `?next` before it falls back to the role home**, which the
 * server-rendered version had no need to: there, a successful log-in was a
 * server redirect and this rule never saw the signed-in caller at all. Here the
 * screen signs somebody in and *then* re-reads `/me`, so this guard is the
 * next thing to run, and sending them home would quietly discard the
 * destination the log-in was started from. It would also strand anybody who
 * opens an old `/login?next=…` link while already signed in.
 *
 * **It does not check whether the role may go there**, on purpose: the
 * destination has a guard of its own, and a member sent to `/bookings` is
 * bounced from there to `/business` by the rule that owns that surface. One
 * extra hop, and no second copy of who-may-see-what living here.
 */
export function guestOnlyAccess(me: WireMe, from = ""): Access {
  if (me.kind === "guest") return ALLOWED;
  const query = from.includes("?") ? from.slice(from.indexOf("?") + 1) : "";
  const next = safeNextPath(new URLSearchParams(query).get("next"));
  return sendTo(next ?? roleHome(me.kind));
}
